import { BrainActivity, BrainStreamEvent } from '@/shared/types/chat';

export type SessionStreamState = {
  sessionId: string | null;
  requestId: string | null;
  settledRequestId: string | null;
  lastSeq: number;
  drafts: Record<string, string>;
  activities: BrainActivity[];
};

export type SessionStreamAction =
  | { type: 'event'; event: BrainStreamEvent }
  | { type: 'reply'; requestId?: string }
  | { type: 'activity_stale'; activityId: string }
  | { type: 'clear'; sessionId: string | null };

export const EMPTY_SESSION_STREAM: SessionStreamState = {
  sessionId: null,
  requestId: null,
  settledRequestId: null,
  lastSeq: -1,
  drafts: {},
  activities: [],
};

const withoutDraft = (drafts: Record<string, string>, streamId: string) =>
  Object.fromEntries(Object.entries(drafts).filter(([id]) => id !== streamId));

const withoutActivity = (state: SessionStreamState, activityId: string): SessionStreamState => {
  const activities = state.activities.filter((activity) => activity.activity_id !== activityId);
  return activities.length === state.activities.length ? state : { ...state, activities };
};

const applyEvent = (state: SessionStreamState, event: BrainStreamEvent): SessionStreamState => {
  switch (event.type) {
    case 'activity.started': {
      const rest = withoutActivity(state, event.data.activity_id);
      return { ...rest, activities: [...rest.activities, event.data] };
    }
    case 'activity.finished':
      return withoutActivity(state, event.data.activity_id);
    case 'text.delta': {
      const { stream_id, text } = event.data;
      return {
        ...state,
        drafts: { ...state.drafts, [stream_id]: (state.drafts[stream_id] ?? '') + text },
      };
    }
    case 'text.discarded':
      return { ...state, drafts: withoutDraft(state.drafts, event.data.stream_id) };
    default:
      return state;
  }
};

const startTurn = (state: SessionStreamState, requestId: string): SessionStreamState => ({
  ...EMPTY_SESSION_STREAM,
  sessionId: state.sessionId,
  requestId,
  settledRequestId: state.requestId ?? state.settledRequestId,
});

const applyStreamEvent = (state: SessionStreamState, event: BrainStreamEvent) => {
  if (event.request_id === state.settledRequestId) {
    return state;
  }
  const current = event.request_id === state.requestId ? state : startTurn(state, event.request_id);
  if (event.seq <= current.lastSeq) {
    // Removing an activity is idempotent, so a late finish still ends it.
    return event.type === 'activity.finished'
      ? withoutActivity(state, event.data.activity_id)
      : state;
  }
  return { ...applyEvent(current, event), lastSeq: event.seq };
};

const settleReply = (state: SessionStreamState, requestId: string | null) => {
  if (requestId === state.requestId) {
    return { ...EMPTY_SESSION_STREAM, sessionId: state.sessionId, settledRequestId: requestId };
  }
  return { ...state, settledRequestId: requestId };
};

export const sessionStreamReducer = (
  state: SessionStreamState,
  action: SessionStreamAction
): SessionStreamState => {
  switch (action.type) {
    case 'event':
      return applyStreamEvent(state, action.event);
    case 'reply':
      return settleReply(state, action.requestId ?? state.requestId);
    case 'activity_stale':
      return withoutActivity(state, action.activityId);
    case 'clear':
      return { ...EMPTY_SESSION_STREAM, sessionId: action.sessionId };
  }
};
